/**
 * O que faz: Normaliza o JSON da RPC de analytics de follow-up e deriva textos/filtros da tela.
 * Onde usa: hook useFollowupAnalytics, cards da aba Follow-up e diálogo de leads disparados.
 * Conexões: followup_analytics no Postgres; FollowupDispatchedLead na lista operacional.
 * Tabelas: nenhuma direta; consome agregado já calculado no servidor.
 * Data: 02/09/2026
 */

import type { FollowupDispatchedLead } from './dispatchedLeads';

export const FILTRO_RESPOSTA_FOLLOWUP = {
  todos: 'todos',
  respondidos: 'respondidos',
  sem_resposta: 'sem_resposta',
} as const;

export type FiltroRespostaFollowup = (typeof FILTRO_RESPOSTA_FOLLOWUP)[keyof typeof FILTRO_RESPOSTA_FOLLOWUP];

export const MINIMO_ENVIOS_INSIGHT_HORA = 8;

export interface FollowupAnalyticsResumo {
  leads_enviados: number;
  leads_respondidos: number;
  leads_sem_resposta: number;
  taxa_resposta_leads: number;
  mensagens_enviadas: number;
  mensagens_respondidas: number;
  taxa_resposta_mensagens: number;
  pendentes: number;
  erros: number;
  canceladas_resposta: number;
  tempo_resposta_mediana_minutos: number | null;
  tempo_resposta_media_minutos: number | null;
  tempo_resposta_p90_minutos: number | null;
}

export interface FollowupAnalyticsFaixa {
  faixa: 'ate_15_min' | 'de_15_min_a_1h' | 'de_1h_a_6h' | 'de_6h_a_24h' | 'acima_24h';
  quantidade: number;
}

export interface FollowupAnalyticsHora {
  hora: number;
  enviadas: number;
  respondidas: number;
  taxa: number;
}

export interface FollowupAnalyticsDia {
  dia: number;
  enviadas: number;
  respondidas: number;
  taxa: number;
}

export interface FollowupAnalyticsEtapa {
  sequencia: number;
  enviadas: number;
  respondidas: number;
  taxa: number;
}

export interface FollowupAnalyticsCampanha {
  campaign_id: string;
  campaign_nome: string;
  ativo: boolean;
  mensagens_enviadas: number;
  mensagens_respondidas: number;
  taxa_resposta_mensagens: number;
  leads_enviados: number;
  leads_respondidos: number;
  taxa_resposta_leads: number;
  pendentes: number;
  erros: number;
  canceladas_resposta: number;
  tempo_resposta_mediana_minutos: number | null;
}

export interface FollowupAnalytics {
  resumo: FollowupAnalyticsResumo;
  faixas_tempo: FollowupAnalyticsFaixa[];
  horarios: FollowupAnalyticsHora[];
  dias: FollowupAnalyticsDia[];
  etapas: FollowupAnalyticsEtapa[];
  campanhas: FollowupAnalyticsCampanha[];
}

export interface FollowupInsight {
  id: string;
  titulo: string;
  texto: string;
}

/** Linha da tabela de campanhas — compatível com o card antigo. */
export interface FollowupCampaignMetric {
  campaign_id: string;
  campaign_nome: string;
  ativo: boolean;
  enviadas: number;
  respondidas: number;
  pendentes: number;
  erros: number;
  canceladas_resposta: number;
  taxa_resposta: number;
  leads_enviados: number;
  leads_respondidos: number;
  taxa_resposta_leads: number;
  tempo_resposta_mediana_minutos: number | null;
}

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = toNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return fallback;
};

const taxa = (enviadas: number, respondidas: number): number => (
  enviadas > 0 ? Math.round((respondidas / enviadas) * 1000) / 10 : 0
);

export const payloadVazioFollowupAnalytics = (): FollowupAnalytics => ({
  resumo: {
    leads_enviados: 0,
    leads_respondidos: 0,
    leads_sem_resposta: 0,
    taxa_resposta_leads: 0,
    mensagens_enviadas: 0,
    mensagens_respondidas: 0,
    taxa_resposta_mensagens: 0,
    pendentes: 0,
    erros: 0,
    canceladas_resposta: 0,
    tempo_resposta_mediana_minutos: null,
    tempo_resposta_media_minutos: null,
    tempo_resposta_p90_minutos: null,
  },
  faixas_tempo: [],
  horarios: [],
  dias: [],
  etapas: [],
  campanhas: [],
});

export const parseFollowupAnalytics = (raw: unknown): FollowupAnalytics => {
  const base = payloadVazioFollowupAnalytics();
  if (!raw || typeof raw !== 'object') return base;

  const source = raw as Record<string, unknown>;
  const resumoRaw = (source.resumo && typeof source.resumo === 'object')
    ? source.resumo as Record<string, unknown>
    : {};

  const leadsEnviados = toNumber(resumoRaw.leads_enviados);
  const leadsRespondidos = toNumber(resumoRaw.leads_respondidos);

  return {
    resumo: {
      leads_enviados: leadsEnviados,
      leads_respondidos: leadsRespondidos,
      leads_sem_resposta: toNumber(resumoRaw.leads_sem_resposta, Math.max(0, leadsEnviados - leadsRespondidos)),
      taxa_resposta_leads: toNumber(resumoRaw.taxa_resposta_leads, taxa(leadsEnviados, leadsRespondidos)),
      mensagens_enviadas: toNumber(resumoRaw.mensagens_enviadas),
      mensagens_respondidas: toNumber(resumoRaw.mensagens_respondidas),
      taxa_resposta_mensagens: toNumber(resumoRaw.taxa_resposta_mensagens),
      pendentes: toNumber(resumoRaw.pendentes),
      erros: toNumber(resumoRaw.erros),
      canceladas_resposta: toNumber(resumoRaw.canceladas_resposta),
      tempo_resposta_mediana_minutos: toNullableNumber(resumoRaw.tempo_resposta_mediana_minutos),
      tempo_resposta_media_minutos: toNullableNumber(resumoRaw.tempo_resposta_media_minutos),
      tempo_resposta_p90_minutos: toNullableNumber(resumoRaw.tempo_resposta_p90_minutos),
    },
    faixas_tempo: Array.isArray(source.faixas_tempo)
      ? source.faixas_tempo.map((item) => {
          const row = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
          return {
            faixa: (toText(row.faixa, 'ate_15_min') as FollowupAnalyticsFaixa['faixa']),
            quantidade: toNumber(row.quantidade),
          };
        })
      : [],
    horarios: Array.isArray(source.horarios)
      ? source.horarios.map((item) => {
          const row = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
          const enviadas = toNumber(row.enviadas);
          const respondidas = toNumber(row.respondidas);
          return {
            hora: toNumber(row.hora),
            enviadas,
            respondidas,
            taxa: toNumber(row.taxa, taxa(enviadas, respondidas)),
          };
        })
      : [],
    dias: Array.isArray(source.dias)
      ? source.dias.map((item) => {
          const row = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
          const enviadas = toNumber(row.enviadas);
          const respondidas = toNumber(row.respondidas);
          return {
            dia: toNumber(row.dia),
            enviadas,
            respondidas,
            taxa: toNumber(row.taxa, taxa(enviadas, respondidas)),
          };
        })
      : [],
    etapas: Array.isArray(source.etapas)
      ? source.etapas.map((item) => {
          const row = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
          const enviadas = toNumber(row.enviadas);
          const respondidas = toNumber(row.respondidas);
          return {
            sequencia: toNumber(row.sequencia),
            enviadas,
            respondidas,
            taxa: toNumber(row.taxa, taxa(enviadas, respondidas)),
          };
        })
      : [],
    campanhas: Array.isArray(source.campanhas)
      ? source.campanhas.map((item) => {
          const row = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
          const mensagensEnviadas = toNumber(row.mensagens_enviadas);
          const mensagensRespondidas = toNumber(row.mensagens_respondidas);
          const leadsEnviadosCampanha = toNumber(row.leads_enviados);
          const leadsRespondidosCampanha = toNumber(row.leads_respondidos);
          return {
            campaign_id: toText(row.campaign_id),
            campaign_nome: toText(row.campaign_nome, 'Campanha sem nome'),
            ativo: Boolean(row.ativo),
            mensagens_enviadas: mensagensEnviadas,
            mensagens_respondidas: mensagensRespondidas,
            taxa_resposta_mensagens: toNumber(row.taxa_resposta_mensagens, taxa(mensagensEnviadas, mensagensRespondidas)),
            leads_enviados: leadsEnviadosCampanha,
            leads_respondidos: leadsRespondidosCampanha,
            taxa_resposta_leads: toNumber(row.taxa_resposta_leads, taxa(leadsEnviadosCampanha, leadsRespondidosCampanha)),
            pendentes: toNumber(row.pendentes),
            erros: toNumber(row.erros),
            canceladas_resposta: toNumber(row.canceladas_resposta),
            tempo_resposta_mediana_minutos: toNullableNumber(row.tempo_resposta_mediana_minutos),
          };
        })
      : [],
  };
};

export const mapCampanhasParaMetricas = (
  campanhas: FollowupAnalyticsCampanha[],
): FollowupCampaignMetric[] => campanhas.map((campanha) => ({
  campaign_id: campanha.campaign_id,
  campaign_nome: campanha.campaign_nome,
  ativo: campanha.ativo,
  enviadas: campanha.mensagens_enviadas,
  respondidas: campanha.mensagens_respondidas,
  pendentes: campanha.pendentes,
  erros: campanha.erros,
  canceladas_resposta: campanha.canceladas_resposta,
  taxa_resposta: campanha.taxa_resposta_mensagens,
  leads_enviados: campanha.leads_enviados,
  leads_respondidos: campanha.leads_respondidos,
  taxa_resposta_leads: campanha.taxa_resposta_leads,
  tempo_resposta_mediana_minutos: campanha.tempo_resposta_mediana_minutos,
}));

export const formatarDuracaoFollowup = (minutos: number | null | undefined): string => {
  if (minutos === null || minutos === undefined || !Number.isFinite(minutos) || minutos < 0) return '—';
  const total = Math.round(minutos);
  const dias = Math.floor(total / 1440);
  const horas = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  if (dias > 0) return horas > 0 ? `${dias} d ${horas} h` : `${dias} d`;
  if (horas > 0) return mins > 0 ? `${horas} h ${mins} min` : `${horas} h`;
  return `${mins} min`;
};

const formatarPercentual = (valor: number): string => (
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(valor)
);

export const montarInsightsFollowup = (dados: FollowupAnalytics): FollowupInsight[] => {
  const insights: FollowupInsight[] = [];
  const { resumo } = dados;

  if (resumo.leads_enviados >= 5) {
    insights.push({
      id: 'taxa-leads',
      titulo: 'Quem respondeu',
      texto: `${resumo.leads_respondidos} de ${resumo.leads_enviados} leads responderam (${formatarPercentual(resumo.taxa_resposta_leads)}%).`,
    });
  }

  if (resumo.tempo_resposta_mediana_minutos !== null) {
    insights.push({
      id: 'tempo-mediana',
      titulo: 'Velocidade',
      texto: `Metade das respostas chegou em até ${formatarDuracaoFollowup(resumo.tempo_resposta_mediana_minutos)}.`,
    });
  }

  const horariosComVolume = dados.horarios.filter((item) => item.enviadas >= MINIMO_ENVIOS_INSIGHT_HORA);
  if (horariosComVolume.length >= 2) {
    const melhor = [...horariosComVolume].sort((a, b) => b.taxa - a.taxa || b.enviadas - a.enviadas)[0];
    const pior = [...horariosComVolume].sort((a, b) => a.taxa - b.taxa || b.enviadas - a.enviadas)[0];
    if (melhor && pior && melhor.hora !== pior.hora && melhor.taxa - pior.taxa >= 10) {
      insights.push({
        id: 'melhor-hora',
        titulo: 'Horário',
        texto: `Envios às ${melhor.hora}h responderam ${formatarPercentual(melhor.taxa)}%. Às ${pior.hora}h a taxa cai para ${formatarPercentual(pior.taxa)}%.`,
      });
    }
  }

  const totalRespostasEtapa = dados.etapas.reduce((soma, etapa) => soma + etapa.respondidas, 0);
  const etapa1 = dados.etapas.find((etapa) => etapa.sequencia === 1);
  if (etapa1 && totalRespostasEtapa >= 5) {
    const participacao = Math.round((etapa1.respondidas / totalRespostasEtapa) * 1000) / 10;
    if (participacao >= 60) {
      insights.push({
        id: 'funil-etapa-1',
        titulo: 'Sequência',
        texto: `A 1ª mensagem concentra ${formatarPercentual(participacao)}% das respostas deste período.`,
      });
    }
  }

  return insights;
};

export const filtrarLeadsPorResposta = (
  leads: FollowupDispatchedLead[],
  filtro: FiltroRespostaFollowup,
): FollowupDispatchedLead[] => {
  if (filtro === FILTRO_RESPOSTA_FOLLOWUP.respondidos) {
    return leads.filter((lead) => Boolean(lead.respondedAt));
  }
  if (filtro === FILTRO_RESPOSTA_FOLLOWUP.sem_resposta) {
    return leads.filter((lead) => !lead.respondedAt);
  }
  return leads;
};

export const calcularMinutosAteResposta = (
  lastSentAt: string,
  respondedAt: string | null,
): number | null => {
  if (!respondedAt) return null;
  const diffMs = new Date(respondedAt).getTime() - new Date(lastSentAt).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;
  return Math.round(diffMs / 60_000);
};
